/**
 * Cloud Operations — frontend API client.
 *
 * Companion to backend Cloud Operations controllers. Every endpoint is gated
 * server-side (PA + Super Admin on reads, PA-only on writes).
 *
 * IMPORTANT — envelope contract. {@link apiFetch} resolves with
 * {@code { ok: true, data, etag }} on success, or throws on most errors.
 * Pages in this section consume the *raw* response shape (e.g. {items:[...],
 * page:0, ...} for a list, or the matrix object for /sla-matrix). The
 * {@link unwrap} helper below normalises the envelope before resolving:
 *   • on {@code ok:true} → resolves with {@code envelope.data}
 *   • on {@code ok:false} → throws an Error built from the structured body
 *
 * Without {@code unwrap}, pages would see the envelope and silently render
 * "no data" (best case) or crash on {@code envelope.tiers.map(...)}
 * (worst case — SLA matrix). Centralising the unwrap here means new endpoints
 * land with the right shape automatically.
 *
 * Design: /agentlets-production/Prove7_Operators_Console_Design.docx §5.
 */

import { apiFetch } from './api';

/**
 * Unwrap an apiFetch envelope.
 *
 * @param {Promise<{ok:boolean,data?:any,error?:string,message?:string,status?:number}>} envelopePromise
 * @returns {Promise<any>} resolves with `envelope.data`; rejects on `ok:false`
 */
const unwrap = (envelopePromise) =>
  envelopePromise.then((envelope) => {
    // Sanity guard — apiFetch should always resolve with an object, but
    // tolerate exotic test mocks.
    if (envelope == null) return null;
    // `ok:false` paths from apiFetch: 409/412/423 structured-body responses
    // (duplicate / etag mismatch / locked). Surface them as errors so the
    // page's .catch() fires consistently with other failure modes.
    if (envelope.ok === false) {
      const msg = envelope.error
        || envelope.message
        || `Cloud Operations request failed (status ${envelope.status ?? 'unknown'})`;
      const err = new Error(msg);
      err.status = envelope.status;
      err.envelope = envelope;
      throw err;
    }
    // `ok:true` happy path — return the raw data the caller expects.
    // Note: `envelope.data` is null for 204 / empty bodies; passing null
    // through is correct (callers guard with `if (!summary) ...`).
    return envelope.data;
  });

// ─── /summary  +  /permissions ───────────────────────────────────────────────
export const getOperatorsSummary = () =>
  unwrap(apiFetch('/api/v1/cloud-operations/summary'));

export const getOperatorsPermissions = () =>
  unwrap(apiFetch('/api/v1/cloud-operations/permissions'));

/**
 * Deployable platform versions (newest first) + the default.
 * Shape: { versions: [{ version, label, latest }], default }
 *
 * Server-side source of truth (PlatformVersionCatalog). NEVER hard-code this
 * list in a component again — the old hard-coded copies drifted behind the
 * release train and would have offered a customer an older build than our own
 * production control plane. DeploymentService rejects versions outside it.
 */
export const getPlatformVersions = () =>
  unwrap(apiFetch('/api/v1/cloud-operations/platform-versions'));

/**
 * Pre-deploy readiness for the given mode/provider.
 * Shape: { execution_mode: 'live'|'dry_run', ready_for_live_deploy, checks: [{id,ok,label,detail}] }
 *
 * The connectors default to dry-run (they return synthetic output and create
 * NOTHING while still reporting success), so this must be surfaced BEFORE the
 * Deploy CTA — not discovered afterwards in the run history.
 */
export const getDeployPreflight = ({ mode, provider } = {}) =>
  unwrap(apiFetch(
    `/api/v1/cloud-operations/preflight?mode=${encodeURIComponent(mode || '')}`
    + `&provider=${encodeURIComponent(provider || '')}`));

// ─── /cloud-accounts ─────────────────────────────────────────────────────────
export const listCloudAccounts = ({ page = 0, size = 25 } = {}) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/cloud-accounts?page=${page}&size=${size}`));

export const getCloudAccount = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/cloud-accounts/${id}`));

export const createCloudAccount = (payload) =>
  unwrap(apiFetch('/api/v1/cloud-operations/cloud-accounts', 'POST', payload));

/**
 * Edit an existing cloud account's trust metadata (name/description/auth_config).
 * Changing auth_config resets the account to pending — re-verify afterward.
 * Used to complete the seeded platform placeholder account before first deploy.
 */
export const updateCloudAccount = (id, payload) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/cloud-accounts/${id}`, 'PUT', payload));

export const verifyCloudAccount = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/cloud-accounts/${id}/verify`, 'POST'));

export const revokeCloudAccount = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/cloud-accounts/${id}`, 'DELETE'));

// ─── /targets ────────────────────────────────────────────────────────────────
export const listTargets = ({ page = 0, size = 25 } = {}) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/targets?page=${page}&size=${size}`));

export const getTarget = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/targets/${id}`));

export const createTarget = (payload) =>
  unwrap(apiFetch('/api/v1/cloud-operations/targets', 'POST', payload));

export const retireTarget = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/targets/${id}`, 'DELETE'));

// ─── /workflows ──────────────────────────────────────────────────────────────
export const listSystemWorkflows = () =>
  unwrap(apiFetch('/api/v1/cloud-operations/workflows'));

export const getSystemWorkflow = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/workflows/${id}`));

// ─── /deployments ────────────────────────────────────────────────────────────
export const listDeployments = ({ page = 0, size = 25 } = {}) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/deployments?page=${page}&size=${size}`));

export const getDeployment = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/deployments/${id}`));

export const listDeploymentRuns = (id, { page = 0, size = 25 } = {}) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/deployments/${id}/runs?page=${page}&size=${size}`));

export const createDeployment = (payload) =>
  unwrap(apiFetch('/api/v1/cloud-operations/deployments', 'POST', payload));

/**
 * Paved-road one-shot deploy. Pairs with the DeployHomePage Configure tab.
 *
 * Server-side, this:
 *  1. For Self-Hosted: find-or-creates a cloud_account_credential by
 *     (provider, account_identifier).
 *  2. Find-or-creates a deployment_target by (name) OR (mode, cloud_account, region, sla_tier).
 *  3. Stashes networking + observability into deployment.config_overrides.
 *  4. Creates the deployment + queues the initial run.
 *
 * The point is to spare operators from chaining three list pages just to
 * launch one cluster. The underlying entities are still queryable via the
 * advanced catalog if they want to manage them later.
 */
export const quickDeploy = (payload) =>
  unwrap(apiFetch('/api/v1/cloud-operations/deployments/quick-deploy', 'POST', payload));

/**
 * The deployments a customer is entitled to provision from their signed order forms.
 * Shape: { entitled: [envelope], provisioned: [envelope] } where each envelope is
 * { orderId, orderNumber, lineId, lineNo, editionKey, modules[], provider, instanceSize,
 *   deploymentMode, region, pairedRegion, dataResidency, slaTier }.
 *
 * `entitled` lines may be fulfilled now (cumulative across every active order form);
 * `provisioned` lines are already deployed and shown read-only. Drives the contract-driven
 * deploy picker — the operator never hand-types infra that a signed order form already fixed.
 */
export const listOrderFormDeployments = (customerId) =>
  unwrap(apiFetch(
    `/api/v1/cloud-operations/order-form-deployments?customerId=${encodeURIComponent(customerId || '')}`));

// ─── /sla-matrix ─────────────────────────────────────────────────────────────
export const getSlaMatrix = () =>
  unwrap(apiFetch('/api/v1/cloud-operations/sla-matrix'));

export const getSlaMatrixForProvider = (provider) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/sla-matrix/${provider}`));

// ─── /bridges ────────────────────────────────────────────────────────────────
export const listBridges = ({ page = 0, size = 25 } = {}) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/bridges?page=${page}&size=${size}`));

export const getBridge = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/bridges/${id}`));

/**
 * Register a Self-Hosted bridge agent. The response carries the
 * registration_token in PLAINTEXT exactly once — store it in a state slot
 * that the user explicitly dismisses; never re-request it.
 */
export const registerBridge = (payload) =>
  unwrap(apiFetch('/api/v1/cloud-operations/bridges', 'POST', payload));

export const revokeBridge = (id) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/bridges/${id}`, 'DELETE'));

// ─── /audit (Operations log) ─────────────────────────────────────────────────
export const listOperationsAudit = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, v);
  });
  return unwrap(apiFetch(`/api/v1/cloud-operations/audit?${qs.toString()}`));
};

// ─── /audit/chains  (V270 — audit chain integrity) ───────────────────────────
export const listAuditChains = () =>
  unwrap(apiFetch('/api/v1/cloud-operations/audit/chains'));

export const verifyAuditChain = (chainKey) =>
  unwrap(apiFetch(`/api/v1/cloud-operations/audit/verify-chain?chain_key=${encodeURIComponent(chainKey)}`));
